import { useState } from "react";
import OuterBlueC from "@/assets/CarlineConsensor-Primary-Square-OuterBlueCNew.svg";
import RedSteer from "@/assets/CarlineConsensor-Primary-Square-RedSteerNew.svg";
import InnerYellowC from "@/assets/CarlineConsensor-PrimarySquare-InnerYellowCNew.svg";
import { useLogoPreload } from "@/hooks/useLogoPreload";

interface LoadingSpinnerProps {
  size?: number | string;
  cycleDuration?: string;
}

export const LoadingSpinner = ({ size = 120, cycleDuration = "8s" }: LoadingSpinnerProps) => {
  const dimension = typeof size === "number" ? `${size}px` : size;
  const assetsReady = useLogoPreload();
  
  const [imagesLoaded, setImagesLoaded] = useState({
    blue: false,
    red: false,
    yellow: false,
  });
  
  const allLoaded = assetsReady && imagesLoaded.blue && imagesLoaded.red && imagesLoaded.yellow;

  return (
    <div
      style={{
        position: "relative",
        width: dimension,
        height: dimension,
        margin: "0 auto",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* No fallback spinner - logo assets are pre-warmed at app mount */}
      <style>
        {`
        .center-origin {
          transform-origin: 50% 50%;
        }

        /*
          Timeline (0–100% of cycle):

          0–5%   : hidden, no spin
          5–35%  : fade IN 0→1 while spinning forward (10 rotations)
          35–50% : fully visible, hold final rotation
          50–80% : fade OUT 1→0 while spinning back to 0deg
          80–100%: hidden, reset at 0deg
        */

        /* BLUE C: forward CW, reverse CCW */
        @keyframes blueCSpinLoop {
          0%   { opacity: 0; transform: rotate(0deg); }

          5%   { opacity: 0; transform: rotate(0deg); }
          35%  { opacity: 1; transform: rotate(3600deg); }   /* fade+spin IN */

          50%  { opacity: 1; transform: rotate(3600deg); }   /* pause visible */

          80%  { opacity: 0; transform: rotate(0deg); }      /* fade+spin OUT */

          100% { opacity: 0; transform: rotate(0deg); }      /* pause hidden */
        }

        /* YELLOW C: forward CCW, reverse CW */
        @keyframes yellowCSpinLoop {
          0%   { opacity: 0; transform: rotate(0deg); }

          5%   { opacity: 0; transform: rotate(0deg); }
          35%  { opacity: 1; transform: rotate(-3600deg); }  /* fade+spin IN */

          50%  { opacity: 1; transform: rotate(-3600deg); }  /* pause visible */

          80%  { opacity: 0; transform: rotate(0deg); }      /* fade+spin OUT */

          100% { opacity: 0; transform: rotate(0deg); }      /* pause hidden */
        }
        `}
      </style>

      {/* BLUE C (bottom) */}
      <img
        src={OuterBlueC}
        alt=""
        className="center-origin"
        onLoad={() => setImagesLoaded(prev => ({ ...prev, blue: true }))}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          zIndex: 1,
          opacity: allLoaded ? 1 : 0,
          animation: allLoaded ? `blueCSpinLoop ${cycleDuration} ease-in-out infinite` : "none",
        }}
      />

      {/* RED STEERING WHEEL (middle, static) */}
      <img
        src={RedSteer}
        alt=""
        className="center-origin"
        onLoad={() => setImagesLoaded(prev => ({ ...prev, red: true }))}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          zIndex: 2,
          opacity: allLoaded ? 1 : 0,
        }}
      />

      {/* YELLOW C (top) */}
      <img
        src={InnerYellowC}
        alt=""
        className="center-origin"
        onLoad={() => setImagesLoaded(prev => ({ ...prev, yellow: true }))}
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          zIndex: 3,
          opacity: allLoaded ? 1 : 0,
          animation: allLoaded ? `yellowCSpinLoop ${cycleDuration} ease-in-out infinite` : "none",
        }}
      />
    </div>
  );
};
